import { formatAmount } from '@common/utils';
import { Calendar, TrendingUp } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { DASHBOARD_COLORS as COLORS } from '../../constants/dashboard.constants';

export function FinancialTrackerChart({
  revenueTrendData,
}: {
  revenueTrendData: { month: string; sortKey: string; paid: number; pending: number }[];
}) {
  return (
    <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
      <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          Évolution Globale (Réalisé vs Attente)
        </h3>
      </div>
      <div className="px-2 pb-2 flex-grow h-[400px]">
        {revenueTrendData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart 
              data={revenueTrendData} 
              margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
              style={{ cursor: 'pointer' }}
            >
              <defs>
                <linearGradient id="colorPaid" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.01} />
                </linearGradient>
                <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.accent} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={COLORS.accent} stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 11, fill: '#94a3b8', fontWeight: 600 }}
                dy={12}
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={60}
                tick={{ fontSize: 11, fill: '#94a3b8', fontWeight: 600 }}
                tickFormatter={(val) => {
                  if (val === 0) return '0';
                  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
                  if (val >= 1000) return `${val / 1000}k`;
                  return val;
                }}
              />
              <Tooltip
                cursor={{ stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '4 4' }}
                content={<CustomTooltip />}
              />
              <Area
                type="monotone"
                name="CA Réalisé"
                dataKey="paid"
                stroke={COLORS.primary}
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorPaid)"
                activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff', fill: COLORS.primary }}
              />
              <Area
                type="monotone"
                name="CA En Attente"
                dataKey="pending"
                stroke={COLORS.accent}
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorPending)"
                activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff', fill: COLORS.accent }}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-slate-400 text-sm gap-2">
            <div className="p-3 bg-slate-50 rounded-full">
              <TrendingUp className="w-5 h-5 text-slate-300" />
            </div>
            <p>Données insuffisantes pour la période sélectionnée.</p>
          </div>
        )}
      </div>

      <div className="px-6 py-4 border-t border-slate-50 flex flex-wrap justify-center items-center gap-x-8 gap-y-2 bg-slate-50/20">
        <div className="flex items-center gap-2.5 transition-opacity hover:opacity-80">
          <div className="w-3.5 h-3.5 rounded-full ring-2 ring-white shadow-sm" style={{ backgroundColor: COLORS.primary }}></div>
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">CA Réalisé</span>
        </div>
        <div className="flex items-center gap-2.5 transition-opacity hover:opacity-80">
          <div className="w-3.5 h-3.5 rounded-full ring-2 ring-white shadow-sm" style={{ backgroundColor: COLORS.accent }}></div>
          <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">CA En Attente</span>
        </div>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }: any) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 backdrop-blur-md border border-slate-200 p-3 rounded-xl shadow-xl min-w-[180px]">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{label}</p>
        <div className="space-y-2">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span className="text-xs font-medium text-slate-600 italic">
                  {entry.name.replace('CA ', '')}
                </span>
              </div>
              <span className="text-sm font-bold text-slate-900">
                {formatAmount(entry.value)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

